import type {
  ParsedInboundVideoStreamStats,
  ParsedInboundAudioStreamStats,
} from 'webrtc-issue-detector';
import type StatsHandler from './StatsHandler';
import type {
  ParsedInboundAudioStreamCompositeStats,
  ParsedInboundVideoStreamCompositeStats,
} from './types';
import type { ExpectedVideoParams, MOSScores } from '../../qoe/types';
import type { ScoresCalculator } from '../../qoe/ScoresCalculator';
import type { WIDStatsAdapter } from '../../qoe/WIDStatsAdapter';
import { getLocalStorageItem } from '../analyticsApiClient/helper';
import BaseTrackStatsManager from './BaseTrackStatsManager';

interface InboundTrackStatsManagerParams {
  track: MediaStreamTrack;
  statsHandler: StatsHandler;
  widStatsAdapter: WIDStatsAdapter;
  scoresCalculator: ScoresCalculator;
  getExpectedVideoParams?: () => ExpectedVideoParams | undefined;
  isStaticContent?: boolean;
}

export default class InboundTrackStatsManager extends BaseTrackStatsManager {
  #videoWebRTCStats?: ParsedInboundVideoStreamCompositeStats;

  #audioWebRTCStats?: ParsedInboundAudioStreamCompositeStats;

  #Qv?: number;

  #Qa?: number;

  readonly #scoresCalculator: ScoresCalculator;

  readonly #statsAdapter: WIDStatsAdapter;

  readonly #getExpectedVideoParams?: () => ExpectedVideoParams | undefined;

  readonly #isStaticContent?: boolean;

  constructor({
    track,
    statsHandler,
    widStatsAdapter,
    scoresCalculator,
    getExpectedVideoParams,
    isStaticContent,
  }: InboundTrackStatsManagerParams) {
    super({ track });

    this.handleWebRTCStats(track, statsHandler);
    this.#statsAdapter = widStatsAdapter;
    this.#scoresCalculator = scoresCalculator;
    this.#getExpectedVideoParams = getExpectedVideoParams;
    this.#isStaticContent = isStaticContent;
  }

  get videoWebRTCStats(): ParsedInboundVideoStreamStats | undefined {
    return this.#videoWebRTCStats?.inbound;
  }

  get audioWebRTCStats(): ParsedInboundAudioStreamStats | undefined {
    return this.#audioWebRTCStats?.inbound;
  }

  get scores(): MOSScores {
    return {
      Qv: this.#Qv,
      Qa: this.#Qa,
    };
  }

  private handleWebRTCStats(track: MediaStreamTrack, statsHandler: StatsHandler): void {
    track.addEventListener('ended', () => {
      statsHandler.removeInboundVideoHandler(track.id);
      statsHandler.removeInboundAudioHandler(track.id);
    }, { once: true });

    if (track.kind === 'video') {
      statsHandler.addInboundVideoHandler(track.id, this.setVideoWebRTCStats.bind(this));

      return;
    }

    statsHandler.addInboundAudioHandler(track.id, this.setAudioWebRTCStats.bind(this));
  }

  private setVideoWebRTCStats(stats: ParsedInboundVideoStreamCompositeStats): void {
    this.touchLastReceivedStatsTimestamp();
    const expectedParams = this.#getExpectedVideoParams?.();
    const preparedStats = this.#statsAdapter.adaptInboundVideoStats({
      stats: stats.inbound,
      connectionStats: stats.connection,
      prevStats: this.#videoWebRTCStats?.inbound,
      expectedWidth: expectedParams?.width,
      expectedHeight: expectedParams?.height,
      expectedFrameRate: expectedParams?.maxFramerate,
      isStaticContent: this.#isStaticContent,
    });
    this.#Qv = this.#scoresCalculator.calculateVideoMOS(preparedStats);
    this.#videoWebRTCStats = stats;
  }

  private setAudioWebRTCStats(stats: ParsedInboundAudioStreamCompositeStats): void {
    this.touchLastReceivedStatsTimestamp();
    const { inbound } = stats;
    const preparedStats = this.#statsAdapter.adaptInboundAudioStats({
      stats: inbound,
      connectionStats: stats.connection,
      fec: getLocalStorageItem('qosInboundFec') ?? true,
      dtx: getLocalStorageItem('qosInboundDtx') ?? true,
      prevStats: this.#audioWebRTCStats?.inbound,
    });
    this.#Qa = this.#scoresCalculator.calculateAudioMOS(preparedStats);
    this.#audioWebRTCStats = stats;
  }

  protected cleanupStats(): void {
    this.#videoWebRTCStats = undefined;
    this.#audioWebRTCStats = undefined;
    this.#Qv = undefined;
    this.#Qa = undefined;
  }
}
